import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import '../styles/Content.css';
import { setPendingNavContext, track } from '../lib/analytics';

const NAV_ITEMS = [
  { id: 'home', label: 'HOME', href: '#home' },
  { id: 'projects', label: 'PROJECTS', href: '#projects' },
  { id: 'about', label: 'ABOUT', href: '#about' },
];

const RESUME_ITEMS = [
  { id: 'resume', label: 'WORK EXPERIENCE', href: '#resume' },
  { id: 'education', label: 'EDUCATION', href: '#education' },
  { id: 'languages', label: 'LANGUAGES', href: '#languages' },
];

const parseActiveFromHash = () => {
  const raw = (window.location.hash || '').replace('#', '').trim().toLowerCase();
  const current = raw || 'home';
  const allowed = new Set([...NAV_ITEMS.map((x) => x.id), 'contact', 'resume', 'education', 'languages']);
  if (allowed.has(current)) return current;
  return 'home';
};

const requestFocus = (section) => {
  window.dispatchEvent(new CustomEvent('ipavlidakis:focus', { detail: { section } }));
};

const Top = () => {
  const [active, setActive] = useState(parseActiveFromHash);
  const [resumeOpen, setResumeOpen] = useState(false);
  const resumeRef = useRef(null);
  const isResumeActive = useMemo(() => ['resume', 'education', 'languages'].includes(active), [active]);

  useEffect(() => {
    const handleHashChange = () => {
      setActive(parseActiveFromHash());
      setResumeOpen(false);
    };
    window.addEventListener('hashchange', handleHashChange);
    handleHashChange();
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  useEffect(() => {
    if (!resumeOpen) return;

    const handlePointerDown = (e) => {
      const root = resumeRef.current;
      if (!root) return;
      if (root.contains(e.target)) return;
      setResumeOpen(false);
    };

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') setResumeOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [resumeOpen]);

  return (
    <header className="site-header">
      <a
        className="brand-pill"
        href="#home"
        onClick={(e) => {
          e.preventDefault();
          setResumeOpen(false);
          const current = parseActiveFromHash();
          const isSame = current === 'home';
          track('nav_click', {
            nav_item: 'brand',
            nav_group: 'brand',
            surface: 'top_brand',
            from_section: current,
            to_section: 'home',
            is_same: isSame,
          });
          if (!isSame) {
            setPendingNavContext({ source: 'nav', surface: 'top_brand', trigger: 'click', nav_item: 'brand' });
          } else {
            track('section_refocus', { section: 'home', source: 'nav', surface: 'top_brand', trigger: 'click', nav_item: 'brand' });
          }
          window.location.hash = '#home';
          if (isSame) requestFocus('home');
        }}
      >
        <span className="brand">IPAVLIDAKIS.DEV</span>
      </a>

      <nav className="nav-pill" aria-label="Primary">
        {NAV_ITEMS.map((item) => (
          <a
            key={item.id}
            className={`nav-item ${active === item.id ? 'is-active' : ''}`}
            href={item.href}
            onClick={(e) => {
              e.preventDefault();
              setResumeOpen(false);
              const current = parseActiveFromHash();
              const isSame = current === item.id;
              track('nav_click', {
                nav_item: item.id,
                nav_group: 'primary',
                surface: 'top_nav',
                from_section: current,
                to_section: item.id,
                is_same: isSame,
              });
              if (!isSame) {
                setPendingNavContext({ source: 'nav', surface: 'top_nav', trigger: 'click', nav_item: item.id });
              } else {
                track('section_refocus', { section: item.id, source: 'nav', surface: 'top_nav', trigger: 'click', nav_item: item.id });
              }
              window.location.hash = item.href;
              if (isSame) requestFocus(item.id);
            }}
          >
            {item.label}
          </a>
        ))}

        <div className="nav-menu" ref={resumeRef}>
          <button
            className={`nav-item nav-menu-button ${isResumeActive ? 'is-active' : ''} ${
              resumeOpen ? 'is-open' : ''
            }`}
            type="button"
            aria-haspopup="menu"
            aria-expanded={resumeOpen}
            onClick={() => {
              const next = !resumeOpen;
              setResumeOpen(next);
              track('resume_menu_toggle', { surface: 'top_resume_menu', is_open: next, active_section: active });
            }}
          >
            RESUME
            <span className="nav-menu-chevron" aria-hidden="true">
              <FontAwesomeIcon icon={faChevronDown} />
            </span>
          </button>

          {resumeOpen ? (
            <div className="nav-menu-popover" role="menu" aria-label="Resume">
              {RESUME_ITEMS.map((item) => (
                <button
                  key={item.id}
                  className={`nav-menu-item ${active === item.id ? 'is-selected' : ''}`}
                  type="button"
                  role="menuitem"
                  onClick={() => {
                    setResumeOpen(false);
                    const current = parseActiveFromHash();
                    const isSame = current === item.id;
                    track('nav_click', {
                      nav_item: item.id,
                      nav_group: 'resume',
                      surface: 'top_resume_menu',
                      from_section: current,
                      to_section: item.id,
                      is_same: isSame,
                    });
                    if (!isSame) {
                      setPendingNavContext({ source: 'nav', surface: 'top_resume_menu', trigger: 'click', nav_item: item.id });
                    } else {
                      track('section_refocus', { section: item.id, source: 'nav', surface: 'top_resume_menu', trigger: 'click', nav_item: item.id });
                    }
                    window.location.hash = item.href;
                    if (isSame) requestFocus(item.id);
                  }}
                >
                  {item.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </nav>
    </header>
  );
};

export default Top;
